import { Badge, Button, cn, ModeToggle, Skeleton } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { Calendar, ChevronRight, MapPin, Pencil, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useCompetitionAuth } from "@/modules/auth/presentation/model/useCompetitionAuth";
import { AdminChangePinDialog } from "@/modules/auth/presentation/ui/AdminChangePinDialog";
import type { AdminEventListItem } from "@/modules/events/presentation/api/queries";
import { useAdminEventsListQuery } from "@/modules/events/presentation/api/queries";
import { CreateEventDialog } from "../ui/CreateEventDialog";
import { EditEventDialog } from "../ui/EditEventDialog";

function EventsListHeader({ onSignOut }: { onSignOut: () => void }) {
  return (
    <header className="kpop-bg flex flex-col gap-3 border-border border-b bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-4">
      <div className="min-w-0">
        <p className="font-bold text-accent text-xs uppercase tracking-widest">
          KiDS EXPO 2026
        </p>
        <h1 className="kpop-gradient-text wrap-break-word font-extrabold text-lg leading-tight sm:text-xl">
          ຈັດການງານການແຂ່ງຂັນ
        </h1>
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
        <AdminChangePinDialog />
        <ModeToggle />
        <Button variant="ghost" size="sm" onClick={onSignOut}>
          ອອກຈາກລະບົບ
        </Button>
      </div>
    </header>
  );
}

function statusBadgeClass(status: string) {
  switch (status) {
    case "scoring":
      return "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
    case "closed":
      return "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300";
    case "published":
      return "border-primary/40 bg-primary/10 text-primary";
    default:
      return "border-muted-foreground/30 bg-muted/50 text-muted-foreground";
  }
}

export function EventsListPage() {
  const { signOut, isCompetitionAdmin } = useCompetitionAuth();
  const navigate = useNavigate();
  const { data: rows, isPending } = useAdminEventsListQuery(
    Boolean(isCompetitionAdmin),
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminEventListItem | null>(null);

  const sorted = useMemo(() => rows ?? [], [rows]);

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: "/competition/login" });
  };

  if (!isCompetitionAdmin) return null;

  const openWorkspace = (ev: AdminEventListItem) => {
    navigate({
      to: "/competition/events/$eventId/admin",
      params: { eventId: ev.id },
    });
  };

  return (
    <div className="flex min-h-svh flex-col bg-background">
      <EventsListHeader onSignOut={handleSignOut} />

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-4 px-6 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-muted-foreground text-sm">
            ເລືອກງານເພື່ອດຳເນີນການຕໍ່ (ທີມ, ຄະແນນ, ປະກາດຜົນ)
          </p>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus data-icon="inline-start" />
            ສ້າງງານໃໝ່
          </Button>
        </div>

        {isPending ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
              <Skeleton key={i} className="h-20 w-full rounded-xl" />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 rounded-2xl bg-card px-8 py-16 text-center ring-1 ring-border">
            <p className="font-medium text-foreground">ຍັງບໍ່ມີງານການແຂ່ງຂັນ</p>
            <p className="text-muted-foreground text-sm">
              ສ້າງງານທຳອິດແລ້ວ ຕັ້ງຊື່, slug ທຸກໆກັບ PIN ກຳມະການ ສາມອັນ — ໃນແຖບ
              ກຳມະການຈະມີລິ້ງເຂົ້າລະບົບແຍກຕາມຄົນ
            </p>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus data-icon="inline-start" />
              ສ້າງງານໃໝ່
            </Button>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {sorted.map((ev) => (
              <li key={ev.id}>
                <div
                  role="presentation"
                  className={cn(
                    "relative flex rounded-2xl bg-card ring-1 ring-border transition-shadow",
                    "hover:shadow-md",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => openWorkspace(ev)}
                    className="flex min-w-0 flex-1 flex-col gap-2 rounded-l-2xl p-4 text-start"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-semibold text-foreground">
                        {ev.name}
                      </span>
                      <Badge className={statusBadgeClass(ev.status)}>
                        {ev.status}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground text-xs">
                      {ev.eventDate ? (
                        <span className="flex items-center gap-1">
                          <Calendar className="size-3 shrink-0" />
                          {ev.eventDate}
                        </span>
                      ) : null}
                      {ev.venue ? (
                        <span className="flex items-center gap-1">
                          <MapPin className="size-3 shrink-0" />
                          <span className="truncate">{ev.venue}</span>
                        </span>
                      ) : null}
                      <span className="text-muted-foreground/80 tabular-nums">
                        {ev.slug}
                      </span>
                    </div>
                  </button>
                  <div className="flex shrink-0 items-stretch rounded-r-2xl border-border border-l">
                    <button
                      type="button"
                      aria-label="ແກ້ໄຂງານ"
                      className="flex items-center px-4 text-muted-foreground hover:bg-muted hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditTarget(ev);
                      }}
                    >
                      <Pencil className="size-4" />
                    </button>
                    <button
                      type="button"
                      aria-label="ເປີດງານດຽວນີ້"
                      className="flex items-center px-4 text-muted-foreground hover:bg-muted hover:text-foreground"
                      onClick={() => openWorkspace(ev)}
                    >
                      <ChevronRight className="size-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>

      <CreateEventDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) =>
          navigate({
            to: "/competition/events/$eventId/admin",
            params: { eventId: id },
          })
        }
      />
      <EditEventDialog
        event={editTarget}
        open={editTarget !== null}
        onOpenChange={(next) => {
          if (!next) setEditTarget(null);
        }}
        onDeleted={() => setEditTarget(null)}
      />
    </div>
  );
}
